import { useContext, useState } from "react";
import useSWR from "swr";
import axios from "axios";
import styled from "styled-components/macro";
import { useTranslation } from "react-i18next";
import { useAuthContext } from "../../../../components/Auth";
import { ErrorPlaceholder } from "../../../../components/Error";
import { Loader } from "../../../../components/Loader/Loader";
import { Notifications } from "../../../../components/Notifications/NotificationsContext";
import type { GetSystemMessagesResponse } from "../../../../types/types";
import { useStoreState, fetcher } from "../../../../util/util";
import { BroadcastCard } from "./BroadcastCard";
import { ConfirmDialog } from "../../../../components/ConfirmDialog/ConfirmDialog";

const BroadcastWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const CustomerBroadcastMessagesContainer = styled.div`
  position: sticky;
  top: 0;
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  border-bottom: ${({ theme }) => theme.resolve("border_sm")} solid
    ${({ theme }) => theme.resolve("color_border_light")};
  padding: ${({ theme }) => theme.resolve("spacing_xl")};
  margin-bottom: ${({ theme }) => theme.resolve("spacing_xl")};
`;

interface BroadcastMessagesProps {
  containerStyles?: React.CSSProperties;
  isCustomer?: boolean;
}

export const DisplayBroadcastMessages = ({
  containerStyles,
  isCustomer = false,
}: BroadcastMessagesProps) => {
  const { t } = useTranslation();
  const { storefront_id } = useStoreState();
  const { roleIsSellerAdmin, roleIsSellerStandard, roleIsSomeKindOfBuyer } =
    useAuthContext();
  const { notifySuccess, notifyError } = useContext(Notifications);

  // State for archive confirmation
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [messageToArchiveId, setMessageToArchiveId] =
    useState<string | null>(null);

  const messagesUrl = `/v2/storefronts/${storefront_id}/system-messages?state=published&unread=true`;
  const {
    data: messagesResponse,
    error: messagesError,
    mutate: mutateMessages,
  } = useSWR<GetSystemMessagesResponse>(messagesUrl, fetcher);

  const canArchive = roleIsSellerAdmin ?? false; // Only admins can archive
  const canDismiss = (roleIsSellerStandard || roleIsSomeKindOfBuyer) ?? false; // Sellers and Buyers can dismiss

  const confirmArchiveHandler = async () => {
    if (!messageToArchiveId || !canArchive) return;
    const url = `/v2/storefronts/${storefront_id}/system-messages/${messageToArchiveId}`;
    try {
      await axios.delete(url);
      notifySuccess(t("Broadcast archived"));
      await mutateMessages();
    } catch (error) {
      console.error("Failed to archive broadcast:", error);
      const message =
        axios.isAxiosError(error) && error.response?.data?.message
          ? error.response?.data?.message
          : t("Failed to archive broadcast. Please try again.");
      notifyError(message, { error });
    } finally {
      // Close dialog after action attempt
      setShowArchiveConfirm(false);
      setMessageToArchiveId(null);
    }
  };

  // New handler to open the dialog
  const handleArchiveClick = (messageId: string) => {
    if (!canArchive) return;
    setMessageToArchiveId(messageId);
    setShowArchiveConfirm(true);
  };

  // New handler to cancel/close the dialog
  const cancelArchiveHandler = () => {
    setShowArchiveConfirm(false);
    setMessageToArchiveId(null);
  };

  const handleDismiss = async (messageId: string) => {
    if (!canDismiss) return; // Extra check
    const url = `/v2/storefronts/${storefront_id}/system-messages/${messageId}/mark-read`;
    try {
      await axios.post(url);
      // Optimistic UI update: remove message immediately from the list
      mutateMessages((currentData) => {
        if (!currentData) return currentData;
        return {
          ...currentData,
          data: currentData.data.filter((msg) => msg.id !== messageId),
        };
      }, false); // 'false' prevents immediate revalidation, relies on optimistic update
      // No success notification typically needed for dismiss
    } catch (error) {
      console.error("Failed to dismiss message:", error);
      const message =
        axios.isAxiosError(error) && error.response?.data?.message
          ? error.response?.data?.message
          : t("Failed to dismiss message. Please try again.");
      notifyError(message, { error });
      // Revert optimistic update on error by revalidating
      await mutateMessages();
    }
  };

  // Handle loading state
  if (!messagesResponse && !messagesError) {
    return <Loader isLoading={true} />;
  }

  // Handle error state
  if (messagesError) {
    return <ErrorPlaceholder message={t("Failed to load messages.")} />;
  }

  const messages = messagesResponse?.data ?? [];

  // Don't render anything if no unread messages
  if (messages.length === 0) {
    return null;
  }

  return isCustomer ? (
    <CustomerBroadcastMessagesContainer>
      <BroadcastWrapper style={containerStyles}>
        {messages.map((message) => (
          <BroadcastCard
            key={message.id}
            message={message}
            // Use handleArchiveClick to trigger dialog
            onArchive={
              canArchive ? () => handleArchiveClick(message.id) : undefined
            }
            onDismiss={canDismiss ? () => handleDismiss(message.id) : undefined}
            canArchive={canArchive}
            showActions={canArchive || canDismiss}
          />
        ))}
        {/* Render Confirmation Dialog */}
        <ConfirmDialog
          show={showArchiveConfirm}
          closeDialog={cancelArchiveHandler}
          handleConfirm={confirmArchiveHandler}
          handleCancel={cancelArchiveHandler}
          confirmMessage={t("Are you sure you want to archive this broadcast?")}
        />
      </BroadcastWrapper>
    </CustomerBroadcastMessagesContainer>
  ) : (
    <BroadcastWrapper style={containerStyles}>
      {messages.map((message) => (
        <BroadcastCard
          key={message.id}
          message={message}
          // Use handleArchiveClick to trigger dialog
          onArchive={
            canArchive ? () => handleArchiveClick(message.id) : undefined
          }
          onDismiss={canDismiss ? () => handleDismiss(message.id) : undefined}
          canArchive={canArchive}
          showActions={canArchive || canDismiss}
        />
      ))}
      {/* Render Confirmation Dialog */}
      <ConfirmDialog
        show={showArchiveConfirm}
        closeDialog={cancelArchiveHandler}
        handleConfirm={confirmArchiveHandler}
        handleCancel={cancelArchiveHandler}
        confirmMessage={t("Are you sure you want to archive this broadcast?")}
      />
    </BroadcastWrapper>
  );
};
